/**
 * Pinboard cards: notes, checklists, images and link previews attached to a plan object.
 * Stored flat in the shared doc (Y.Map "cards", one Y.Map per card) so two people can edit
 * different cards at the same time without overwriting each other.
 */

export type CardType = "note" | "check" | "image" | "link";

export interface CheckItem { id: string; text: string; done: boolean }

export interface CardImage {
  /** sha256 of the (resized) image file — the image lives in IndexedDB / on the server under this hash */
  hash: string;
  w: number;
  h: number;
  mime: string;
  name?: string;
}

export interface CardLink {
  url: string;
  title?: string;
  description?: string;
  site?: string;
  /** preview image URL (server copy) */
  image?: string;
  /** the site blocked previews (e.g. Instagram) */
  blocked?: boolean;
}

export interface BoardCard {
  id: string;
  objectId: string;
  type: CardType;
  /** position/size on the board canvas (board pixels) */
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  color?: string;
  text?: string;
  items?: CheckItem[];
  image?: CardImage;
  link?: CardLink;
  author: string;
  createdAt: number;
  updatedAt: number;
}

export const CARD_SIZE: Record<CardType, [number, number]> = {
  note: [220, 160],
  check: [240, 200],
  image: [260, 200],
  link: [260, 230],
};

export const NOTE_COLORS = ["#fff3a8", "#ffd6a5", "#caffbf", "#a0e7ff", "#e2c6ff", "#ffffff"];

const TYPES: CardType[] = ["note", "check", "image", "link"];
const HASH_RE = /^[0-9a-f]{64}$/;
const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
const str = (v: unknown, max = 5000) => (typeof v === "string" ? v.slice(0, max) : undefined);

/** Validate a card from a file / clipboard. Returns null if unusable. */
export function parseCard(raw: unknown, newId: () => string): BoardCard | null {
  const c = raw as Partial<BoardCard>;
  if (!c || typeof c !== "object" || !TYPES.includes(c.type as CardType) || typeof c.objectId !== "string") return null;
  const [dw, dh] = CARD_SIZE[c.type as CardType];
  const now = Date.now();
  const out: BoardCard = {
    id: typeof c.id === "string" && c.id ? c.id : newId(),
    objectId: c.objectId,
    type: c.type as CardType,
    x: num(c.x, 0), y: num(c.y, 0), w: Math.max(120, num(c.w, dw)), h: Math.max(80, num(c.h, dh)), z: num(c.z, 0),
    author: str(c.author, 60) ?? "",
    createdAt: num(c.createdAt, now),
    updatedAt: num(c.updatedAt, now),
  };
  if (typeof c.color === "string" && /^#[0-9a-fA-F]{6}$/.test(c.color)) out.color = c.color;
  if (c.text !== undefined) out.text = str(c.text) ?? "";
  if (Array.isArray(c.items)) out.items = c.items.slice(0, 200).map((i) => ({ id: str(i?.id, 40) || newId(), text: str(i?.text, 500) ?? "", done: !!i?.done }));
  if (c.image && HASH_RE.test(c.image.hash)) out.image = { hash: c.image.hash, w: num(c.image.w, 0), h: num(c.image.h, 0), mime: str(c.image.mime, 40) ?? "image/webp", ...(c.image.name ? { name: str(c.image.name, 200) } : {}) };
  if (c.link && typeof c.link.url === "string" && /^https?:\/\//.test(c.link.url)) {
    const l = c.link;
    out.link = { url: l.url.slice(0, 2000), ...(l.title ? { title: str(l.title, 300) } : {}), ...(l.description ? { description: str(l.description, 500) } : {}), ...(l.site ? { site: str(l.site, 100) } : {}), ...(l.image && /^https?:\/\//.test(l.image) ? { image: l.image.slice(0, 2000) } : {}), ...(l.blocked ? { blocked: true } : {}) };
  }
  if (out.type === "image" && !out.image) return null;
  if (out.type === "link" && !out.link) return null;
  return out;
}

/** Where to put a new card: right of the existing ones, wrapping into rows. */
export function nextCardPosition(cards: BoardCard[], type: CardType): { x: number; y: number } {
  if (!cards.length) return { x: 24, y: 24 };
  const [w] = CARD_SIZE[type];
  const right = Math.max(...cards.map((c) => c.x + c.w));
  if (right + 24 + w <= 1100) return { x: right + 24, y: Math.min(...cards.map((c) => c.y)) };
  const bottom = Math.max(...cards.map((c) => c.y + c.h));
  return { x: 24, y: bottom + 24 };
}

/** First place at or near (x, y) where a w×h card doesn't cover another card (steps right, then down). */
export function freeSpot(cards: Pick<BoardCard, "x" | "y" | "w" | "h">[], x: number, y: number, w: number, h: number): { x: number; y: number } {
  const hit = (px: number, py: number) => cards.some((c) => px < c.x + c.w + 8 && px + w + 8 > c.x && py < c.y + c.h + 8 && py + h + 8 > c.y);
  for (let row = 0; row < 12; row++)
    for (let col = 0; col < 6; col++) {
      const px = x + col * (w + 16), py = y + row * 40;
      if (!hit(px, py)) return { x: px, y: py };
    }
  return { x: x + 24, y: y + 24 };
}

/** Does a pasted text look like a web link? */
export function looksLikeUrl(s: string) {
  return /^https?:\/\/\S+$/i.test(s.trim());
}
